/**
 * The signed request in a confirm link (`/confirm#r=<payload>.<signature>`, made by the gate for an API key or an AI
 * agent). It lives in the fragment so no server ever logs it, and it has to stay that way through sign-in: the
 * sign-in page carries `next` in its address and hands it to the sign-in provider, so the fragment must never be in
 * `next`. `keepConfirmLink` runs before the router reads the address: it moves the request into this tab's
 * sessionStorage and takes it out of the address, so `/confirm` is all the router (and `next`) ever sees.
 *
 * A link opened in another tab (a mailed sign-in link opens one) isn't there: the page then asks the person to open
 * the link again, which works now that they're signed in.
 */

const KEY = "adea.confirmLink";
/** `<payload>.<signature>`, both base64url. */
const SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/;
const PATH = /^\/confirm\/?$/;

/** Kept in memory too, for a browser that won't store anything (a private window with storage blocked). */
let held: string | null = null;

function fromHash(hash: string): string | null {
  const r = new URLSearchParams(hash.replace(/^#/, "")).get("r");
  return r && SHAPE.test(r) ? r : null;
}

/** At start-up: a confirm link's request moves from the address into this tab, and the address keeps only `/confirm`. */
export function keepConfirmLink(): void {
  if (typeof window === "undefined" || !PATH.test(window.location.pathname)) return;
  if (!window.location.hash) return;
  const r = fromHash(window.location.hash);
  if (r) {
    held = r;
    try {
      sessionStorage.setItem(KEY, r);
    } catch {
      // storage blocked: the page still works in this load
    }
  }
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
}

/** The request the person opened, or null when there is none in this tab. */
export function confirmLinkRequest(): string | null {
  if (held) return held;
  try {
    const r = sessionStorage.getItem(KEY);
    return r && SHAPE.test(r) ? r : null;
  } catch {
    return null;
  }
}

/** Once the link is used, turned down or can't be used, this tab forgets it. */
export function forgetConfirmLink(): void {
  held = null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing kept
  }
}
